import {
  CARD,
  IMAGE_MAP,
  LayoutColumnsDefinition,
  LayoutListDefinition,
  LAYOUT_COLUMNS,
  LAYOUT_LIST,
  POPUP,
  PopupDefinition,
  TEXT,
  AUDIO_POPUP,
  AudioPopupDefinition
} from '../items';
import { Translator } from '../Translator';
import { templateTextFieldsEnabled } from '../utils/templateTextFields';
import { translate } from './translate';
import { useEditorStore } from '~/shell/Store';
import { isMobile } from 'react-device-detect';
import {
  INTERACTIVE4_BTN_SIZE as BTN_SIZE,
  INTERACTIVE4_MAP_H,
  INTERACTIVE4_MAP_W,
  clampHotspotPixelsToImage,
  resolveInteractive4Overlaps,
  usableHotspotImageSize
} from './interactive4Overlap';
import { resolveHotspotMarker } from './resolveHotspotMarker';
import {
  KICKER_HINT,
  OPTIONAL_TEXT_FIELDS,
  optionalFieldsInEditor,
  optionalFieldValues
} from '../utils/optionalFields';

export const Interactive4: Translator = sequence => {
  const optional = optionalFieldsInEditor(OPTIONAL_TEXT_FIELDS);
  const { editorMode, contentEditable } = useEditorStore.getState();

  const columns = {
    type: LAYOUT_COLUMNS,
    id: `${sequence.id}-columns`,
    items: [],
    values: {}
  } as LayoutColumnsDefinition;

  let maxWidth = 1200;
  let maxHeight = 800;

  let originMaxWidth = 1200;
  let originMaxHeight = 800;

  const getCoordinatesInPercentage = (
    left: number,
    top: number,
    maxWidth: number,
    maxHeight: number
  ) => {
    return {
      left: (left * 100) / maxWidth,
      top: (top * 100) / maxHeight
    };
  };

  /**
   * https://linear.app/easyllama/issue/ENG-9451/go1-support-issue-content-icons-overlapping
   *
   * The Problem:
   * - Sometimes the content team changes the image size without updating the popup coordinates.
   * - Or they change the popup coordinates without checking how they are displayed.
   * - The overlapping of buttons has a significant negative impact on the user experience.
   *
   * To address this, I implemented an algorithm that automatically shifts buttons when an overlap is detected.
   *
   * Resolve overlapping popup buttons by nudging them minimally while
   * preserving the original spatial pattern.
   *
   * 1. Sort buttons by proximity to the top-left corner (anchor first).
   * 2. For each non-anchor button, find the worst overlap and nudge it
   *    on the axis needing the least displacement, in its natural
   *    direction.  The move is validated against the target conflict
   *    before it is accepted; fallback strategies (opposite direction,
   *    other axis, diagonal) are tried in order.
   * 3. Every button is processed per pass; passes repeat until stable.
   *
   * Runs whenever there are 2+ hotspots so editor (AudioPoint) and learner (POPUP / AUDIO_POPUP) share the same nudged layout.
   */
  const root = translate(sequence, item => {
    switch (item.__typename) {
      case 'ImageMap': {
        let clampImageW: number | null = null;
        let clampImageH: number | null = null;

        if (
          item.pictures &&
          item.pictures.length &&
          item.pictures?.[0]?.mediaUrl
        ) {
          /** 1×1 solid backgrounds are unusable as a % basis → keep defaults. */
          const intrinsicSize = usableHotspotImageSize(
            item.pictures?.[0]?.width,
            item.pictures?.[0]?.height
          );

          if (intrinsicSize) {
            maxWidth = intrinsicSize.width;
            maxHeight = intrinsicSize.height;

            originMaxWidth = maxWidth;
            originMaxHeight = maxHeight;

            clampImageW = intrinsicSize.width;
            clampImageH = intrinsicSize.height;
          } else {
            // Solid 1×1 backgrounds: editor stores pixel coords on a 1024 basis
            originMaxWidth = 1024;
            originMaxHeight = 1024;
          }
        }

        if (!editorMode) {
          maxWidth = 400;
          maxHeight = 420;
        }

        const popupItems = item.items || [];

        const positions = popupItems.map(i => {
          const parts = (i.coordinates || '').split(',');
          const clamped = clampHotspotPixelsToImage(
            parseInt(parts[0], 10) || 0,
            parseInt(parts[1], 10) || 0,
            clampImageW,
            clampImageH
          );
          const coord = getCoordinatesInPercentage(
            clamped.x,
            clamped.y,
            originMaxWidth,
            originMaxHeight
          );
          // Same base % for editor and learner so ImageMap / AudioPoint matches POPUP layout.
          let left = coord.left;
          let top = coord.top;
          if (isMobile) {
            if (left > 80) left = 80;
            if (top > 80) top = 80;
          }
          return { left, top };
        });

        if (positions.length > 1) {
          resolveInteractive4Overlaps(positions, isMobile);
        }

        columns.items.push({
          id: 'LAYOUT_LIST',
          type: LAYOUT_LIST,
          values: { align: 'center' },
          items: [
            {
              type: CARD,
              id: '1',
              values: {
                clearStyles: 'lg',
                // Keep 400×420 aspect (hotspot % + overlap math). Shrink width only —
                // height follows via aspect-ratio so cover/hotspots stay aligned at 320 CSS px.
                width: `min(${INTERACTIVE4_MAP_W}px, 100%)`,
                height: 'auto',
                aspectRatio: `${INTERACTIVE4_MAP_W} / ${INTERACTIVE4_MAP_H}`,
                padding: '0px'
              },
              items: [
                {
                  type: IMAGE_MAP,
                  id: item.id,
                  values: {
                    imageUrl: item.pictures?.[0]?.mediaUrl,
                    w: '100%',
                    h: '100%',
                    /** Anchored POPUP/AUDIO_POPUP: no extra +100px drift; keep card inside photo on ultrawide */
                    clampPopupToImageMap: true,
                    popupFixedOffsetLeftPx: 0,
                    popupFixedOffsetTopPx: 20,
                    // Learners see a kicker on a hotspot without audio; the
                    // panel edits it for a company with the template text
                    // fields.
                    ...(contentEditable && templateTextFieldsEnabled()
                      ? { offerPopupSubtitle: true }
                      : {})
                  },
                  items: popupItems.map((i, index) => {
                    const _left = positions[index].left;
                    const _top = positions[index].top;

                    if (i.audios && i.audios.length > 0) {
                      const numericTitle =
                        i.title &&
                        typeof i.title === 'string' &&
                        /^\d+$/.test(i.title.trim())
                          ? i.title.trim()
                          : null;
                      const legacyMarkerTitle =
                        item.variant === 'number' ? index + 1 : numericTitle;

                      return {
                        type: AUDIO_POPUP,
                        id: `${(i as Record<string, unknown>).id ?? item.id}-${index}`,
                        items: [],
                        values: {
                          title: resolveHotspotMarker(
                            (i as { iconType?: string | null }).iconType,
                            (i as { iconValue?: number | null }).iconValue,
                            legacyMarkerTitle
                          ),
                          iconType:
                            (i as { iconType?: string | null }).iconType ??
                            null,
                          iconValue:
                            (i as { iconValue?: number | null }).iconValue ??
                            null,
                          left: _left,
                          top: _top,
                          name: numericTitle ? '' : i.title,
                          body: i.body,
                          mediaUrl: i.audios?.[0]?.mediaUrl,
                          audioId: i.audios?.[0]?.id
                            ? Number(i.audios[0].id)
                            : undefined,
                          subtitlesUrl: i.audios?.[0]?.subtitlesUrl,
                          blocking: true,
                          replay: true,
                          isPopupCentered: isMobile,
                          openPopupUnderButton: !isMobile,
                          popupFixedOffsetLeftPx: 0,
                          popupFixedOffsetTopPx: 20,
                          btnSize: BTN_SIZE
                        }
                      } as AudioPopupDefinition;
                    } else {
                      const numericTitle =
                        i.title &&
                        typeof i.title === 'string' &&
                        /^\d+$/.test(i.title.trim())
                          ? i.title.trim()
                          : null;
                      const legacyBtnTitle =
                        item.variant === 'number'
                          ? index + 1
                          : numericTitle || '+';

                      return {
                        type: POPUP,
                        id: `${(i as Record<string, unknown>).id ?? item.id}-${index}`,
                        items: [],
                        values: {
                          btnTitle: String(
                            resolveHotspotMarker(
                              (i as { iconType?: string | null }).iconType,
                              (i as { iconValue?: number | null }).iconValue,
                              legacyBtnTitle,
                              '+'
                            ) ?? '+'
                          ),
                          iconType:
                            (i as { iconType?: string | null }).iconType ??
                            null,
                          iconValue:
                            (i as { iconValue?: number | null }).iconValue ??
                            null,
                          left: _left,
                          top: _top,
                          title: i.title,
                          subtitle: i.subtitle,
                          body: i.body,
                          blocking: true,
                          btnSize: BTN_SIZE,
                          isPopupCentered: isMobile,
                          openPopupUnderButton: !isMobile,
                          popupFixedOffsetLeftPx: 0,
                          popupFixedOffsetTopPx: 20,
                          metric: '%'
                        }
                      } as PopupDefinition;
                    }
                  })
                }
              ]
            }
          ]
        });
        break;
      }
      case 'Text':
      case 'Question':
        columns.items.push({
          id: 'LAYOUT_LIST_RIGHT',
          type: LAYOUT_LIST,
          values: { align: 'left', padded: true },
          items: [
            {
              type: TEXT,
              id: `${item.id}_subtitle`,
              items: [],
              values: {
                ...optionalFieldValues(optional, 'subtitle', KICKER_HINT),
                value: item.subtitle,
                variant: 'sub_heading',
                icon: item.icon
              }
            },
            {
              type: TEXT,
              id: `${item.id}_title`,
              items: [],
              values: {
                ...optionalFieldValues(optional, 'title'),
                value: item.title,
                variant: 'heading1'
              }
            },
            {
              type: TEXT,
              id: `${item.id}_body`,
              items: [],
              values: {
                ...optionalFieldValues(optional, 'body'),
                value: item.body
              }
            }
          ]
        } as LayoutListDefinition);
        break;
    }
    return null;
  });

  root.items.push(columns);
  return root;
};
